import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import Text from 'antd/lib/typography/Text';
import Dropdown from 'antd/lib/dropdown';
import Button from 'antd/lib/button';
import { MoreOutlined } from '@ant-design/icons';

import { CombinedState } from 'reducers';
import { Project } from 'cvat-core-wrapper';
import Preview from 'components/common/preview';
import { fromNow, plural, t } from 'utils/i18n';
import ProjectActionsMenuComponent from './actions-menu';

interface Props {
    project: Project;
}

export function taskCountLabel(count: number | null): string {
    if (count === null) return '';
    return plural(count, 'task');
}

// "3 tasks · 2 references" on one line, "updated 2 days ago" under it
export function cardMeta(count: number | null, references: number | null): string {
    const parts = [taskCountLabel(count)];
    if (references !== null) parts.push(plural(references, 'reference'));
    return parts.filter(Boolean).join(' · ');
}

export function updatedLabel(updated: string | undefined): string {
    return updated ? t('updated {ago}', { ago: fromNow(updated) }) : '';
}

export default function ProjectCard({ project }: Props): JSX.Element {
    const history = useHistory();
    const deletes = useSelector((state: CombinedState) => state.projects.activities.deletes);
    const deleted = project.id in deletes ? deletes[project.id] : false;
    const open = (): void => history.push(`/projects/${project.id}`);

    return (
        <div className={`cvat-projects-card${deleted ? ' cvat-projects-card-deleted' : ''}`} data-project={project.id}>
            <Preview
                project={project}
                loadingClassName='cvat-projects-card-preview-loading'
                emptyPreviewClassName='cvat-projects-card-preview-empty'
                previewWrapperClassName='cvat-projects-card-preview'
                previewClassName='cvat-projects-card-preview-image'
                onClick={open}
            />
            <div className='cvat-projects-card-body'>
                <button type='button' className='cvat-projects-card-title' onClick={open}>{project.name}</button>
                <div className='cvat-projects-card-meta'>
                    <Text type='secondary' className='cvat-projects-card-counts'>
                        {cardMeta(project.taskCount, project.referenceCount)}
                        <span className='cvat-projects-card-updated'>{updatedLabel(project.updatedDate)}</span>
                    </Text>
                    <Dropdown destroyPopupOnHide trigger={['click']} overlay={<ProjectActionsMenuComponent projectInstance={project} />}>
                        <Button type='text' size='small' aria-label={t('More')} icon={<MoreOutlined />} />
                    </Dropdown>
                </div>
            </div>
        </div>
    );
}
